import {css, StyleSheet} from 'aphrodite';
import Backbone from 'backbone';
import moment from 'moment';
import 'moment-timezone';
import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';

import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import QualityDataLegend from 'molbio/proteins/alphafold/QualityDataLegend';
import ProteinStore from 'molbio/proteins/ProteinStore';
import {ProteinStructureExternalFile} from 'molbio/proteins/util/ProteinConstants';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {RemainingExecutionsAndTenantNameQuery} from './__generated/RemainingExecutionsAndTenantNameQuery';
import AlphaFoldTabContent, {REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY} from './AlphaFoldTabContent';
import AlphaFoldTabState from './AlphaFoldTabState';
import {downloadStructureFile, getStructureInfoFromVersions} from './AlphaFoldTabUtils';

function getTooltipText(tenantName: string): string {
  const timeUnit = BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC ? 'year' : 'month';
  // Get the datetime info for the next moment when this user's AlphaFold runs will reset.
  const date = moment.tz('America/Los_Angeles').add(1, timeUnit).startOf(timeUnit);

  const tenantQualifier = BenchlingConfig.MOLBIO_ALPHAFOLD_IS_ACADEMIC ? '' : ` across ${tenantName}`;
  return `You have a total of ${
    BenchlingConfig.MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT
  } requests per ${timeUnit}${tenantQualifier}. Requests will reset back to ${
    BenchlingConfig.MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT
  } on ${date.format('LT z')} at the start of every calendar ${timeUnit}.`;
}

class AlphaFoldOptionsConnector extends DataConnector<
  {proteinId: string},
  {structureFile: ProteinStructureExternalFile | null}
> {
  shouldSubscribeToRedux = false;
  stores = [ProteinStore];

  enhanceProps(): {
    structureFile: ProteinStructureExternalFile | null;
    proteinId: string;
    structureProteinVersionId: number | null;
  } {
    const {structureFile, versionId} = getStructureInfoFromVersions(
      this.proteinStore().getVersionsById(this.props.proteinId)
    );
    const proteinId = this.props.proteinId;
    return {structureFile, proteinId, structureProteinVersionId: versionId};
  }
}

export function AlphaFoldOptions({
  structureFile,
  proteinId,
  structureProteinVersionId,
}: {
  structureFile: ProteinStructureExternalFile | null;
  proteinId: string;
  structureProteinVersionId: number | null;
}): JSX.Element {
  const queryResult = useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(
    REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY
  );
  const [popoverProps, setPopoverProps] = useState<
    {isOpen: boolean; interaction: FloatingInteraction.CONTROLLED} | {interaction: FloatingInteraction.HOVER}
  >({
    isOpen: true,
    interaction: FloatingInteraction.CONTROLLED,
  });
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPopoverProps({interaction: FloatingInteraction.HOVER});
    }, 5000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  if (structureFile != null && structureProteinVersionId != null) {
    return (
      <div className={css(styles.optionButtonsLayout)}>
        <div>
          <Popover
            {...popoverProps}
            placement={FloatingPlacement.RIGHT_START}
            renderContent={() => <QualityDataLegend />}
          >
            <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
          </Popover>
          <span className={css(styles.infoText)}>Model confidence</span>
        </div>
        <Button
          className={css(styles.withoutButtonFocusStyling)}
          text={`Download ${
            structureFile.previewType === EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER ? 'mmCIF (PDBx)' : 'PDB'
          }`}
          buttonStyle={ButtonStyle.PRIMARY}
          size={ButtonSize.MINI}
          onClick={() => downloadStructureFile(structureFile, proteinId, structureProteinVersionId)}
        />
      </div>
    );
  } else {
    const quota = BenchlingConfig.MOLBIO_ALPHAFOLD_EXECUTION_COUNT_LIMIT;
    return (
      <div className={css(styles.optionButtonsLayout)}>
        {queryResult.type === QueryResultType.SUCCESS && (
          <div>
            <Tooltip title={getTooltipText(queryResult.data.currentTenant.displayName)}>
              <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
            </Tooltip>
            <span className={css(styles.infoText)}>
              {queryResult.data.remainingAlphafoldExecutions} of {quota} requests remaining
            </span>
          </div>
        )}
      </div>
    );
  }
}

const ConnectedAlphaFoldOptions = AlphaFoldOptionsConnector.connect(AlphaFoldOptions);

class AlphaFoldOptionsView extends Backbone.View<AlphaFoldTabState> {
  render(): this {
    const proteinId = this.model.protein().id;
    renderToDOM(<ConnectedAlphaFoldOptions proteinId={proteinId} />, this.el);
    return this;
  }

  remove(): Backbone.View<AlphaFoldTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

class AlphaFoldTabView extends Backbone.View<AlphaFoldTabState> {
  static initClass(): void {
    this.prototype.className = 'buffer-tab buffer-tab--no-padding';
  }

  render(): this {
    renderToDOM(<AlphaFoldTabContent proteinId={this.model.protein().id} />, this.el);
    return this;
  }

  remove(): Backbone.View<AlphaFoldTabState> {
    ReactDOM.unmountComponentAtNode(this.el);
    return super.remove();
  }
}

AlphaFoldTabView.initClass();

const styles = StyleSheet.create({
  infoIcon: {
    color: GRAY_DARKER,
  },
  infoText: {
    ...FONT_PROXIMA_13,
    marginLeft: '8px',
  },
  optionButtonsLayout: {
    padding: '0px 7px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // Should match the value inherited from .buffer-options
    height: '35px',
  },
  largerText: FONT_PROXIMA_13,
  withoutButtonFocusStyling: {':focus': {textDecoration: 'none'}},
});

export default {tabView: AlphaFoldTabView, optionsView: AlphaFoldOptionsView};
